<script setup lang="ts">
import type { AlgoliaOffer } from '@global-types/algoliaOffer';
import OfferCard from '@components/OfferCard/OfferCard.vue';
import FavoriteButton from '@components/FavoriteButton.vue';
import { computed } from 'vue';
import { useSearchStore } from '@stores/useSearchStore';
import { trans, transchoice } from '@tools/translation';
import { OfficeOfferPriceType } from '@enums/officeOfferPriceType';
import { OfficeStateEnum } from '@organisms/offer/office-state-enum';
import { PriceTypeMode } from '@enums/priceTypeMode.ts';
import { isPriceDropRecent } from '@organisms/offer/offer-utils';

const { offer } = defineProps<{ offer: AlgoliaOffer }>();
const searchStore = useSearchStore();

const { addFavorite, removeFavorite, isFavorite } = searchStore;

const onFavoriteChange = async (value: boolean): Promise<void> => {
  if (value) {
    addFavorite(offer.hash_id);
    return;
  }

  removeFavorite(offer.hash_id);
};

const getTypeInfos = (): string | null => {
  if (!offer.display_type) {
    return null;
  }

  let typeInfos = trans(`offer_card.display_types.${offer.display_type}`);

  if (offer.display_type !== 'flex_operator') {
    typeInfos = `${trans('offer_card.display_types.private')} • ${typeInfos.toLowerCase()}`;
  }

  return typeInfos;
};

const getOfferInfos = (): string => {
  const capacityLabel = offer.capacity
    ? `${offer.capacity} ${transchoice('global.post_office', { count: offer.capacity })}`
    : '';

  const surfaceLabel = offer.surface
    && offer.price_type !== OfficeOfferPriceType.RENT_WORKSTATION
    ? `${offer.surface} ${trans('global.meter.square')}`
    : '';

  return [ capacityLabel, surfaceLabel ].filter(Boolean)
    .join(' • ');
};

const deserializedOffer = computed<Record<string, unknown>>(() => {
  const isRentSurface = offer.price_type === OfficeOfferPriceType.RENT_SURFACE;

  return {
    ...offer,
    name: offer.title,
    public_url: offer.uri,
    total_price: !isRentSurface ? offer.price : undefined,
    monthly_total_price: isRentSurface ? offer.legacy_price : undefined,
    state: OfficeStateEnum.PUBLISHED,
    type_infos: getTypeInfos(),
    offer_infos: getOfferInfos(),
    address: {
      ...offer.address,
      latitude: offer._geoloc.lat,
      longitude: offer._geoloc.lng,
    },
    is_flex_operator_display_type: offer.display_type === 'flex_operator',
    first_station_distance: {
      unique_lines: offer.address.first_station?.unique_lines ?? [],
    },
    is_accessible: true,
    availability_date: {
      iso: new Date(offer.availability_date.unix * 1000).toISOString(),
      unix: offer.availability_date.unix,
    },
    is_price_drop_recent: isPriceDropRecent(offer.price_dropped_at),
  };
});
</script>

<template>
  <OfferCard
    class="search-offer-card"
    :first-offer="deserializedOffer"
    :price-suffix="PriceTypeMode.FULL"
    :width="581"
    :image-ratio="[ 1, 1 ]"
  >
    <template #actions>
      <FavoriteButton
        :offer-hash-id="offer.hash_id"
        :is-active="isFavorite(offer.hash_id)"
        @change="onFavoriteChange"
      />
    </template>
  </OfferCard>
</template>

<style lang="scss" scoped>
.search-offer-card {
  @apply m-0 min-w-[250px];
}

:deep(.offer-card__slider img) {
  @apply h-[230px] w-full;

  @apply object-cover;
}
</style>
